import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { useTheme, Button, Tab, Tabs, Tooltip } from '@mui/material';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { PageLayout } from '../../PageLayout';
import { TabPanel } from '../../Atoms/TabPanel';
import {
  getSelectedTab,
  updateState,
  ApiAdvancedFilter,
  FilterableField,
  PaginatedResponse
} from '../../../store/GeneralStore';
import { fetchLinks, getTallyLinksSubject, Link } from '../../../store/Link';
import { getCurrentUser, UserRole } from '../../../store/User';
import {
  getCustomizationSubject,
  getCustomViewSubject,
  CustomView,
  PrefKey,
  UserCustomizations
} from '../../../store/UserCustomizations';
import { DefaultLinkGridColumnDefs } from './LinkGridColumnDefs';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { Menu as CustomViewMenu } from '../../Compounds/CustomTableViews/Menu';
import {
  buildAdvancedFilterSet,
  getColsForTab,
  getInitialState,
  mergeAndDeduplicateFilters
} from '../../Compounds/UpstackDataGrid/helpers';
import { DEFAULT_TAB_SX_STYLES } from '../../../utils/styleHelpers';
import { BulkAddLinksModal } from './BulkAddLinksModal';

export interface TabProps {
  name: string;
  customView?: boolean;
  filter?: FilterableField[];
  tooltip?: string;
  disabledFilters?: string[];
}

export const LinkPage = () => {
  const [linksLoading, setLinksLoading] = useState<boolean>(false);
  const [links, setLinks] = useState<PaginatedResponse<Link>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations | undefined>();
  const [selectedTab, setSelectedTab] = useState(getSelectedTab().links);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewNames, setCustomViewNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [createBulkOpen, setCreateBulkOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [newlyCreatedViewName, setNewlyCreatedViewName] = useState<string | null>(null);

  const theme = useTheme();
  const filterKeys = useMemo(
    () =>
      DefaultLinkGridColumnDefs()
        .filter((column) => column.filterable)
        .map((column) => column.field),
    []
  );

  const defaultTabs = [
    {
      name: 'Active',
      filter: [
        { operator: 'is', field: 'candidate', value: 'false' },
        { operator: 'is', field: 'archived', value: 'false' }
      ],
      tooltip: 'candidate is false, archived is false',
      disabledFilters: ['candidate', 'archived']
    },
    {
      name: 'Candidate',
      filter: [
        { operator: 'is', field: 'candidate', value: 'true' },
        { operator: 'is', field: 'archived', value: 'false' }
      ],
      tooltip: 'candidate is true, archived is false',
      disabledFilters: ['candidate', 'archived']
    },
    {
      name: 'Archived',
      filter: [{ operator: 'is', field: 'archived', value: 'true' }],
      tooltip: 'archived is true',
      disabledFilters: ['archived']
    }
  ] as TabProps[];

  const allAvailableViews = useMemo(() => {
    const customViewsTabProps: TabProps[] = customViews
      .filter((view) => view.page === 'linkGrid_')
      .map((t) => ({ name: t.name, customView: true }));

    return [...defaultTabs, ...customViewsTabProps];
  }, [customViews]);

  const handleFetchLinks = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (localLoading) setLinksLoading(true);

      let currentTabFilters: FilterableField[] = [];
      const currentSelectedView = allAvailableViews[selectedTab];

      if (currentSelectedView) {
        currentTabFilters = (currentSelectedView.filter as FilterableField[]) || [];
      }

      await fetchLinks(
        {
          ...filters,
          filters: mergeAndDeduplicateFilters(filters?.filters || [], currentTabFilters, true)
        },
        forceRefresh
      );
      if (localLoading) setLinksLoading(false);
    },
    [allAvailableViews, selectedTab]
  );

  const handleTabChange = useCallback(
    (index: number, name: string, type: 'main' | 'custom') => {
      if (type === 'main') {
        setSelectedCustomView('');
      } else if (type === 'custom') {
        setSelectedCustomView(name);
      }

      const actualIndex = allAvailableViews.findIndex((tab) => {
        if (type === 'custom') {
          return tab.name === name && tab.customView;
        } else {
          return tab.name === name && !tab.customView;
        }
      });

      if (actualIndex !== -1) {
        updateState({
          selectedTab: {
            ...getSelectedTab(),
            links: actualIndex
          }
        });
        setSelectedTab(actualIndex);
      }
    },
    [allAvailableViews]
  );

  const getInitialSort = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `linkGrid_${pageName}` as PrefKey;
      const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
      return savedSort?.length ? savedSort : ([] as GridSortModel);
    },
    [allAvailableViews, selectedTab]
  );

  const getInitialFilter = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `linkGrid_${pageName}` as PrefKey;
      const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
      return savedFilter || ({} as GridFilterModel);
    },
    [allAvailableViews, selectedTab]
  );

  const dataFetch = useCallback(() => {
    if (userCustomizations && allAvailableViews[selectedTab]) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchLinks(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel
            ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
            : undefined
        },
        true,
        true
      );
    }
  }, [
    userCustomizations,
    allAvailableViews,
    selectedTab,
    getInitialSort,
    getInitialFilter,
    handleFetchLinks,
    filterKeys
  ]);

  const handleNewViewCreated = useCallback((viewName: string) => {
    setNewlyCreatedViewName(viewName);
  }, []);

  useEffect(() => {
    const linkSub = getTallyLinksSubject().subscribe((a) => setLinks(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));
    const customViewsSub = getCustomViewSubject().subscribe((custViews) => setCustomViews(custViews));
    return () => {
      if (linkSub) linkSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (customViewsSub) customViewsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setCustomViewNames(customViews.filter((view) => view.page === 'linkGrid_').map((v) => v.name));
  }, [customViews]);

  useEffect(() => {
    if (allAvailableViews.length > 0 && selectedTab !== undefined && selectedTab >= 0) {
      const initialSelectedView = allAvailableViews[selectedTab];
      if (initialSelectedView) {
        if (initialSelectedView.customView) {
          setSelectedCustomView(initialSelectedView.name);
        } else {
          setSelectedCustomView('');
        }
      }
    }
  }, [allAvailableViews, selectedTab]);

  useEffect(() => {
    if (newlyCreatedViewName) {
      const newViewIndex = allAvailableViews.findIndex((tab) => tab.name === newlyCreatedViewName && tab.customView);
      if (newViewIndex !== -1) {
        handleTabChange(newViewIndex, newlyCreatedViewName, 'custom');
        setNewlyCreatedViewName(null);
      }
    }
  }, [newlyCreatedViewName, allAvailableViews, handleTabChange]);

  useEffect(() => {
    if (userCustomizations && allAvailableViews.length > 0) {
      dataFetch();
    }
  }, [selectedTab, userCustomizations, allAvailableViews, dataFetch]);

  useEffect(() => {
    if (selectedCustomView && !customViewNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      handleTabChange(0, 'Active', 'main');
    }
  }, [selectedCustomView, customViewNames, allAvailableViews, handleTabChange]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewTabModalOpen = () => {
    setCreateOpen(true);
  };

  const handleCustomViewMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.customView);
      if (index !== -1) {
        handleTabChange(index, name, 'custom');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  // Create internal links with role-based visibility
  const currentUser = getCurrentUser();
  const isAdminOrEngineer = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.ENGINEER;

  const internalLinks = [
    {
      url: '/link/bulk-creation-by-asset-fields',
      display: 'Bulk Add Links by Asset Field',
      visible: isAdminOrEngineer
    },
    {
      display: 'Bulk Add Links by ID List',
      visible: isAdminOrEngineer,
      onHandleClick: () => {
        setCreateBulkOpen(true);
      }
    },
    {
      url: '/link/bulk-archive',
      display: 'Bulk Archive Links',
      visible: isAdminOrEngineer
    }
  ];

  return (
    <PageLayout
      className="w-full mt-0"
      dataCy="link-page"
      dataTestId="link-page"
      pageTitle="Links"
      showInternalLinks={true}
      internalLinks={internalLinks}>
      <Fragment>
        <Tabs
          sx={DEFAULT_TAB_SX_STYLES}
          value={selectedTab}
          scrollButtons="auto"
          onChange={(_e, index) => {
            const currentTab = allAvailableViews[index];
            if (currentTab) {
              if (currentTab.customView) {
                handleTabChange(index, currentTab.name, 'custom');
              } else {
                handleTabChange(index, currentTab.name, 'main');
              }
            }
          }}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: theme.palette.primary.main,
                height: allAvailableViews[selectedTab]?.customView ? 0 : 2,
                borderRadius: 1
              }
            }
          }}>
          {defaultTabs.map((tab, _index) => (
            <Tab
              sx={{
                color: 'grey9',
                fontSize: '.8rem',
                textTransform: 'none'
              }}
              key={tab.name}
              label={
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</span>}
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none',
                        width: 'auto'
                      }
                    }
                  }}>
                  <span>{tab.name}</span>
                </Tooltip>
              }
            />
          ))}
        </Tabs>
        <div className="-mt-12 float-right inline-flex pr-4">
          <div className={`flex ${selectedCustomView ? 'border-b-2 border-indigo' : ''}`}>
            <div
              key="custom-views"
              className="self-center">
              <CustomViewMenu
                setSelectedView={setSelectedCustomView}
                selectedView={selectedCustomView}
                viewOptions={customViewNames}
                handleViewChange={handleCustomViewMenuChange}
                defaultTitle="My Views"
              />
            </div>
          </div>
          <div className="ml-3 self-center">
            <Button
              onClick={() => setTriggerSave(true)}
              startIcon={<AddCircleIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Save As View
            </Button>
          </div>
          <div
            className="ml-3 self-center"
            key="add-custom-view">
            <Button
              onClick={() => setOpen(true)}
              startIcon={<EditNoteIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Edit Views
            </Button>
          </div>
        </div>
        {allAvailableViews.map((tab, index) => {
          let tabKeyName = tab.name || '';
          if (tab && !tab.customView) {
            tabKeyName = tabKeyName.replace(/\s/g, '');
          }
          const tabKey = `linkGrid_${tabKeyName}` as PrefKey;
          const colsForTab = getColsForTab(DefaultLinkGridColumnDefs(), tab.disabledFilters ?? []);
          const gridSettingsForTab = userCustomizations?.content?.[tabKey];
          let initialState;
          if (gridSettingsForTab) {
            initialState = getInitialState(gridSettingsForTab, colsForTab, tabKey);
            const initialFilterModel = getInitialFilter(userCustomizations);
            if (initialState && initialFilterModel) {
              if (initialState.filter) {
                initialState.filter.filterModel = initialFilterModel;
              } else {
                initialState.filter = { filterModel: initialFilterModel };
              }
            }
          }

          return (
            <Fragment key={tab.name}>
              {!initialState ||
                (linksLoading && (
                  <div className="flex h-[45rem] m-auto">
                    <ComponentSpinner label="Loading data..." />
                  </div>
                ))}
              {!linksLoading && initialState && (
                <TabPanel
                  key={tab.name}
                  index={index}
                  value={selectedTab}
                  className="max-w-[60vw] min-w-full">
                  <UpstackDataGrid
                    advancedFiltering={true}
                    apiFilter={true}
                    columns={colsForTab}
                    fetchFunction={handleFetchLinks}
                    filterKeys={filterKeys}
                    gridSettings={gridSettingsForTab}
                    handleNewTabModalOpen={handleNewTabModalOpen}
                    initialState={initialState}
                    page={tabKey}
                    rows={links?.data || []}
                    rowCount={links?.totalCount}
                    showSearch={false}
                    title={`Links - ${tab.name}`}
                    triggerSave={triggerSave}
                    handleSaveComplete={() => setTriggerSave(false)}
                  />
                </TabPanel>
              )}
            </Fragment>
          );
        })}
      </Fragment>
      <ViewsModal
        open={open}
        handleClose={handleClose}
        type="link"
      />
      <NewViewModal
        onViewCreated={handleNewViewCreated}
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allAvailableViews[selectedTab]?.name || 'Active'}
        sourceTabFilter={allAvailableViews[selectedTab]?.filter}
        type="link"
      />
      <BulkAddLinksModal
        open={createBulkOpen}
        setOpen={setCreateBulkOpen}
        changeTab={handleTabChange}
      />
    </PageLayout>
  );
};
